import { evaluateActionCapability } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import {
  useParticipantCreateMutation,
  useSessionCreateMutation,
  useCohortLifecycleMutations,
  useUserLifecycleMutations
} from "../hooks/useCoachingQueries";
import { useArtifactWorkflowMutations } from "../hooks/useArtifactWorkflowMutations";
import { useOrganizationLifecycleMutations } from "../hooks/useOrganizationLifecycleMutations";
import { asLiveApiId, useLiveCoachingList } from "../hooks/useLiveProductData";
import { LiveFormModule, toOptions, userOptions } from "./product-live-form-module";

/**
 * Live-mode create lanes for the primary human flows. These render only in
 * live data mode; see product-live-form-module.tsx for the shared posture
 * (capability gates, UUID idempotency keys, CO- reference rendering).
 */

export function LiveCompanyCreateForm({ role }: { role: CoachingRole }) {
  const { createCompany } = useOrganizationLifecycleMutations();

  return (
    <LiveFormModule
      title="Create company"
      subtitle="Creates a live company record in this organization."
      entityLabel="company"
      decision={evaluateActionCapability(role, "companies:create")}
      submitLabel="Create company"
      fields={[{ name: "name", label: "Company name", required: true, hint: "Editable only for admin roles." }]}
      onSubmit={async (values) => createCompany.mutateAsync({ name: values.name })}
    />
  );
}

export function LiveProgramCreateForm({ role }: { role: CoachingRole }) {
  const { createProgram } = useOrganizationLifecycleMutations();
  const companies = useLiveCoachingList("companies.list");
  const users = useLiveCoachingList("users.list");

  return (
    <LiveFormModule
      title="Create program"
      subtitle="Programs link a company with an owning coach."
      entityLabel="program"
      decision={evaluateActionCapability(role, "programs:create")}
      submitLabel="Create program"
      fields={[
        { name: "name", label: "Program name", required: true },
        {
          name: "companyId",
          label: "Owning company",
          required: true,
          options: toOptions(companies.items.map((company) => ({ id: company.id, label: company.name }))),
          optionsEmptyHint: "No live companies exist yet — create a company first."
        },
        {
          name: "coachId",
          label: "Assigned coach",
          required: true,
          options: userOptions(users.items),
          optionsEmptyHint: "No live users are visible to assign as coach."
        }
      ]}
      onSubmit={async (values) =>
        createProgram.mutateAsync({
          name: values.name,
          companyId: asLiveApiId(values.companyId),
          coachId: asLiveApiId(values.coachId)
        })
      }
    />
  );
}

export function LiveCohortCreateForm({ role }: { role: CoachingRole }) {
  const { create } = useCohortLifecycleMutations();
  const companies = useLiveCoachingList("companies.list");
  const programs = useLiveCoachingList("programs.list");
  const users = useLiveCoachingList("users.assignableCoaches", { enabled: role === "coach" || role === "admin" || role === "super_admin" });

  return (
    <LiveFormModule
      title="Create cohort"
      subtitle="Cohorts require a company, program, and coach before session work."
      entityLabel="cohort"
      decision={evaluateActionCapability(role, "cohorts:create")}
      submitLabel="Create cohort"
      fields={[
        { name: "name", label: "Cohort name", required: true },
        {
          name: "companyId",
          label: "Company",
          required: true,
          options: toOptions(companies.items.map((company) => ({ id: company.id, label: company.name }))),
          optionsEmptyHint: "No live companies exist yet — create a company first."
        },
        {
          name: "programId",
          label: "Program",
          required: true,
          options: toOptions(programs.items.map((program) => ({ id: program.id, label: program.name }))),
          optionsEmptyHint: "No live programs exist yet — create a program first."
        },
        {
          name: "coachId",
          label: "Assigned coach",
          required: true,
          options: userOptions(users.items),
          optionsEmptyHint: "No live users are visible to assign as coach."
        }
      ]}
      onSubmit={async (values) =>
        create.mutateAsync({
          name: values.name,
          companyId: asLiveApiId(values.companyId),
          programId: asLiveApiId(values.programId),
          coachId: asLiveApiId(values.coachId)
        })
      }
    />
  );
}

export function LiveParticipantCreateForm({ role }: { role: CoachingRole }) {
  const createParticipant = useParticipantCreateMutation();
  const cohorts = useLiveCoachingList("cohorts.list");

  return (
    <LiveFormModule
      title="Create participant"
      subtitle="Invites a learner into a live cohort; identity stays with the auth service."
      entityLabel="participant"
      decision={evaluateActionCapability(role, "participants:create")}
      submitLabel="Create participant"
      fields={[
        { name: "email", label: "Email", type: "email", required: true, hint: "We'll use this address for the account invite." },
        { name: "name", label: "Display name" },
        {
          name: "cohortId",
          label: "Cohort membership",
          required: true,
          options: toOptions(cohorts.items.map((cohort) => ({ id: cohort.id, label: cohort.name }))),
          optionsEmptyHint: "No live cohorts exist yet — create a cohort first."
        }
      ]}
      onSubmit={async (values) =>
        createParticipant.mutateAsync({
          email: values.email,
          ...(values.name ? { name: values.name } : {}),
          cohortId: asLiveApiId(values.cohortId)
        })
      }
    />
  );
}

export function LiveSessionCreateForm({ role }: { role: CoachingRole }) {
  const createSession = useSessionCreateMutation();
  const cohorts = useLiveCoachingList("cohorts.list");

  return (
    <LiveFormModule
      title="Create session"
      subtitle="Schedules one live coaching session inside a cohort."
      entityLabel="session"
      decision={evaluateActionCapability(role, "sessions:create")}
      submitLabel="Create session"
      fields={[
        {
          name: "cohortId",
          label: "Cohort",
          required: true,
          options: toOptions(cohorts.items.map((cohort) => ({ id: cohort.id, label: cohort.name }))),
          optionsEmptyHint: "No live cohorts exist yet — create a cohort first."
        },
        { name: "title", label: "Session title" },
        { name: "scheduledDate", label: "Session date", type: "date", required: true },
        { name: "scheduledTime", label: "Start time", type: "time", required: true }
      ]}
      onSubmit={async (values) =>
        // The live contract reads scheduledDate/scheduledTime; the previous
        // sessionDate/startTime keys were silently dropped, which left created
        // sessions without a schedule (the "no live signal" Start column).
        createSession.mutateAsync({
          cohortId: asLiveApiId(values.cohortId),
          ...(values.title ? { title: values.title } : {}),
          scheduledDate: values.scheduledDate,
          scheduledTime: values.scheduledTime
        })
      }
    />
  );
}

export function LiveUserCreateForm({ role }: { role: CoachingRole }) {
  const { create } = useUserLifecycleMutations();

  return (
    <LiveFormModule
      title="Create coach user"
      subtitle="Admin-created users start scoped; only super admins can grant admin."
      entityLabel="user"
      decision={evaluateActionCapability(role, "users:create")}
      submitLabel="Create user"
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "email", label: "Email", type: "email", required: true, hint: "Identity service owns final verification." },
        {
          name: "role",
          label: "Assignable role",
          required: true,
          options: [
            { value: "coach", label: "Coach" },
            { value: "admin", label: "Admin (super admin grant only)" }
          ],
          hint: "The live service refuses admin grants from non-super-admin actors."
        }
      ]}
      onSubmit={async (values) => create.mutateAsync({ name: values.name, email: values.email, role: values.role })}
    />
  );
}

export function LiveResourceCreateForm({ role }: { role: CoachingRole }) {
  const { createResource } = useArtifactWorkflowMutations();

  return (
    <LiveFormModule
      title="Create resource"
      subtitle="Registers a global resource record; binary upload completion stays readiness-gated."
      entityLabel="resource"
      decision={evaluateActionCapability(role, "resources:manage")}
      submitLabel="Create resource"
      fields={[
        { name: "title", label: "Title", required: true },
        { name: "resourceType", label: "Resource type", required: true, hint: "For example: pdf, checklist, link." },
        { name: "url", label: "Resource URL", required: true, hint: "Where the published material lives." },
        { name: "description", label: "Description" }
      ]}
      onSubmit={async (values) =>
        createResource.mutateAsync({
          title: values.title,
          resourceType: values.resourceType,
          url: values.url,
          ...(values.description ? { description: values.description } : {})
        })
      }
    />
  );
}
